import "../../css/App.css";
import "../../css/FullEvent/FullEvent.css";
import "../../css/FullEvent/ShareTickets.css";
import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import {
  AccountData,
  Event,
  EventType,
  TicketReceipt,
  TicketV2,
  TwilioPhoneVerificationStatus,
} from "@markit/common.types";
import {
  GetDateWithTime,
  getCanonicalMediaUrls,
  isProductSpecificType,
  MARKIT_CONTACT_US,
  MARKIT_HOTLINE_NUMBER,
  userPhoneVerificationStatus,
  isProductNonPostType,
} from "@markit/common.utils";
import {
  formatPhoneNumber,
  isValidUSPhoneNumber,
} from "../../utils/FormatPhoneNumber";
import {
  getUserEventFreeTicketsMapped,
  getUserTicket,
  userCurrentNumberOfPendingTickets,
  userCurrentNumberOfTickets,
} from "../../utils/eventUtils/userTicketUtils";
import Footer from "../../components/Footer";
import Calendar from "../../components/Calendar";
import CreatorTextManual from "../../components/CreatorTextManual";
import {
  DataLoaders,
  getLoadedEvent,
  getLoadedUser,
  getLoadedUsers,
} from "../../redux/slices/dataSlice";
import {
  useAccountSelector,
  useEventSelector,
  useParametricSelector,
} from "../../hooks/useParametricSelector";
import LoadingScreen from "../LoadingScreen";
import { useQuery } from "react-query";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  LoginState,
  accountActions,
  getAccountState,
} from "../../redux/slices/accountSlice";
import { isRCA } from "../../utils/globalUtils";
import { downloadMediaFromFirestore } from "../../utils/mediaUtils";
import { getUrlParamState } from "../../redux/slices/urlParamSlice";
import { Colors } from "../../utils/colors";
import { Icon } from "@iconify/react";
import { useTheme } from "../../hooks/useTheme";
import {
  getTicketData,
  getTicketsRef,
  getUserData,
} from "../../utils/FirebaseUtils";
import { API } from "../../API";
import RectangleButton from "../../components/Buttons/RectangleButton";
import TopHeader from "../../components/TopHeader";
import { FullEventManageTickets } from "../../components/FullEvent/FullEventManageTickets";
import { getDocs, limit, query, where } from "../../firebase";
import Banner, { BannerType } from "../../components/Banner";
import FullEventHosts from "../../components/FullEvent/FullEventHosts";
import FullEventTimeLocation from "../../components/FullEvent/FullEventTimeLocation";
import StandardBorderedContainer from "../../components/Containers/StandardBorderedContainer";
import FullEventOrganizerConfirmation from "../../components/FullEvent/FullEventOrganizerConfirmation";
import { GuestListSummary } from "../../components/FullEventSubComponents/GuestListSummary";
import {
  checkIfUserIsUnsubscribed,
  checkIfUserIsUnsubscribedMarkit,
} from "../../utils/userUtils";
import OptedOutBanner from "../../components/Verification/OptedOutBanner";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import { NavigationId } from "../../navigation/AppParamList";
import { checkIsDesktop } from "../../utils/screenResponsivenessUtils";
import { useWindowResize } from "../../hooks/useWindowResize";
import EventItemImage from "../../components/DisplayItem/EventItem/EventItemImage";
import ConfirmationProductMedia from "../../components/FullEvent/ConfirmationProductMedia";
import UnlockProductByPhoneNumber from "../../components/CreatorDashboard/Products/FullProduct/UnlockProductByPhoneNumber";
import { HorizontalDivider } from "../../components/Dividers/HorizontalDivider";
import FullEventConfirmationReceipts from "../../components/FullEvent/FullEventConfirmationReceipts";
import ExpandableBox from "../../components/Containers/ExpandableBox";
import { testStripe } from "../../utils/stripeAccountUtils";
import DropdownArrowIcon from "../../components/DropdownsAndTabs/DropdownArrowIcon";
import { LoginStage } from "../Login/LoginWelcome";

const FullEventConfirmation = memo(function FullEventConfirmationFn() {
  const dispatch = useDispatch();
  const location = useLocation() || {};
  const { pathname } = location;
  const [searchParams] = useSearchParams();
  const { ticketId } = useSelector(getUrlParamState);
  const navigate = useNavigate();
  const showTwilioIssuesMessage = false;
  const [mappedTickets, setMappedTickets] = useState<Map<string, TicketV2[]>>(
    new Map()
  );
  const [numOfTickets, setNumOfTickets] = useState(0);
  const [numOfApprovedTickets, setNumOfApprovedTickets] = useState(0);
  const [numOfPendingTickets, setNumOfPendingTickets] = useState(0);
  const [roleUserData, setRoleUserData] = useState<AccountData>();
  const [alreadyHaveRole, setAlreadyHaveRole] = useState<boolean>();
  const [ticketReceipts, setTicketReceipts] = useState<TicketReceipt[]>([]);
  const [optedOut, setOptedOut] = useState(false);
  const [optedOutCreator, setOptedOutCreator] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showConfirmationMobile, setShowConfirmationMobile] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { account } = useSelector(getAccountState);
  const { isRCA: isRCABool, loggedIn } = account;

  const refContainer = useRef<HTMLDivElement>(null);

  const windowWidth = useWindowResize();

  const isDesktopSize = useMemo(
    () => checkIsDesktop(windowWidth),
    [windowWidth]
  );

  const styles = {
    leftContainer: {
      gap: 24,
      width: isDesktopSize ? 380 : "100%",
    },
    rightContainer: {
      padding: 14,
      borderRadius: 20,
      width: 362,
      alignSelf: "flex-start",
      position: "sticky",
      top: 24,
    },
  };

  const eventId = useEventSelector();
  const {
    isLoading: isLoadingEventData,
    data: event,
    isError: isErrorEventData,
  } = useParametricSelector(getLoadedEvent, eventId);
  const userId = useAccountSelector();
  const {
    isLoading: isLoadingUserData,
    data: userData,
    isError: isErrorUserData,
  } = useParametricSelector(getLoadedUser, userId);
  const {
    isLoading: isLoadingHostData,
    data: host,
    isError: isErrorHostData,
  } = useParametricSelector(getLoadedUser, event?.createdBy ?? "");

  const cohostsList: AccountData[] = useParametricSelector(
    getLoadedUsers,
    event?.cohosts ?? []
  )
    .map((user) => user?.data)
    .filter((user) => user !== undefined) as AccountData[];

  const hostAndCohosts: AccountData[] = useMemo(
    () => (cohostsList && host ? [host, ...cohostsList] : host ? [host] : []),
    [cohostsList, host]
  );

  const onViewEventClick = useCallback(() => {
    window.open(`https://markitai.com/e/${eventId}`, "_blank", "noreferrer");
  }, [eventId]);

  const { theme } = useTheme();

  const isTicketStatusPending = useMemo(
    () => numOfApprovedTickets === 0 && numOfTickets !== 0,
    [numOfApprovedTickets, numOfTickets]
  );

  const isProductConfirmation = useMemo(
    () => event && isProductSpecificType(event.eventType),
    [event]
  );

  // Video, audio and pdf products show their media in the order details
  const isMediaProduct = useMemo(
    () =>
      event &&
      isProductNonPostType(event.eventType) &&
      event.eventType !== EventType.CUSTOM_PRODUCT,
    [event]
  );

  // Unlocked when arriving straight from checkout, when signed in as the
  // attendee, or (non-US numbers, who can't sign in) after entering their phone number
  const [unlockedByPhone, setUnlockedByPhone] = useState(false);
  const isUnlockedFromCheckout = !!location.state?.unlocked;
  const isSignedInAsAttendee =
    loggedIn === LoginState.LOGGED_IN &&
    !!userData &&
    account.accountData.uid === userData.uid;
  const isMediaLocked =
    !!isMediaProduct &&
    !(isUnlockedFromCheckout || unlockedByPhone || isSignedInAsAttendee);
  const isUSAttendee = useMemo(
    () => !!userData && isValidUSPhoneNumber(userData.phoneNumber),
    [userData]
  );

  // Ticket holders can always download their product from the confirmation
  // page, regardless of mediaDownloadable (which only hides the button on
  // the FullProduct page)
  const showDownloadButton = useMemo(
    () =>
      !!isProductConfirmation &&
      event?.eventType !== EventType.CUSTOM_PRODUCT &&
      numOfApprovedTickets > 0 &&
      !isMediaLocked,
    [
      isProductConfirmation,
      numOfApprovedTickets,
      event?.eventType,
      isMediaLocked,
    ]
  );

  const downloadContent = useCallback(async () => {
    if (!event) return;
    const mediaUrls = getCanonicalMediaUrls(event);
    if (mediaUrls.length > 0) {
      setIsDownloading(true);
      // Only download the first media file due to issues with downloading multiple files at once on mobile
      await downloadMediaFromFirestore(mediaUrls.slice(0, 1), event.title);
      setIsDownloading(false);
    }
  }, [event]);

  const isUserRCA = useMemo(
    () => (event && isRCA(event.createdBy, event.cohosts)) ?? false,
    [event]
  );

  const { data: roleTicket } = useQuery(
    ["roleTicket", eventId, userId],
    async () => {
      const foundTicketId = ticketId || searchParams.get("ticketId");
      if (eventId && userId) {
        const userTicket: TicketV2 | undefined = await getUserTicket(
          eventId,
          userId
        );
        if (userTicket && userTicket.customTicketId === "") {
          const fetchedRoleUserData = await getUserData(userTicket.uid);
          setRoleUserData(fetchedRoleUserData);
          // in case someone was trying to redeem a role even if they already have one
          if (
            loggedIn === LoginState.LOGGED_IN &&
            foundTicketId &&
            foundTicketId !== userTicket.id
          ) {
            setAlreadyHaveRole(true);
          }
        }
        if (foundTicketId) {
          const unassignedRoleTicket = await getTicketData(
            eventId,
            foundTicketId
          );
          if (
            unassignedRoleTicket &&
            unassignedRoleTicket.customTicketId === ""
          ) {
            if (unassignedRoleTicket.uid !== "") {
              const fetchedRoleUserData = await getUserData(
                unassignedRoleTicket.uid
              );
              setRoleUserData(fetchedRoleUserData);
            }
            return unassignedRoleTicket;
          }
        }
      }
      return undefined;
    }
  );

  // used for when a user has one ticket and we need to display the correct one-click actions on confirmation page
  const { data: userTicket } = useQuery(
    ["userTicket", eventId, userId],
    async () => {
      if (eventId && userId) {
        const ticketsRef = getTicketsRef(eventId);
        const ticketsQuery = query(
          ticketsRef,
          where("uid", "==", userId),
          limit(1)
        );
        const ticket = (await getDocs(ticketsQuery)).docs.map((doc) =>
          doc.data()
        )[0];
        return ticket;
      }
      return undefined;
    }
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (event == null) {
      if (isErrorEventData) {
        setLoading(false);
      }
      return;
    }
    if (host == null) {
      if (isErrorHostData) {
        setLoading(false);
      }
      return;
    }
    if (userData == null) {
      if (isErrorUserData) {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    dispatch(accountActions.setIsRCA(isUserRCA));
    const [
      numTickets,
      numPendingTickets,
      receiptsResponse,
      mappedEventTickets,
      isUnsubscribedCreator,
      isUnsubscribedMarkit,
    ] = await Promise.all([
      userCurrentNumberOfTickets(event.id, userData.uid),
      userCurrentNumberOfPendingTickets(event.id, userData.uid),
      (testStripe(host.uid)
        ? API.testPayment.fetchTicketReceipts
        : API.payment.fetchTicketReceipts)({
        attendeeUid: userData.uid,
        eventId: event.id,
      }), // paid tickets
      getUserEventFreeTicketsMapped(event, userData.uid), // free/request tickets
      checkIfUserIsUnsubscribed(userData.uid, event.createdBy),
      checkIfUserIsUnsubscribedMarkit(userData.phoneNumber),
    ]);
    const { receipts } = receiptsResponse;

    setNumOfApprovedTickets(numTickets);
    setNumOfPendingTickets(numPendingTickets);
    setNumOfTickets(numTickets + numPendingTickets);
    setTicketReceipts(receipts);
    setMappedTickets(mappedEventTickets);
    setOptedOut(isUnsubscribedMarkit);
    setOptedOutCreator(isUnsubscribedCreator);
    setLoading(false);
  }, [
    dispatch,
    event,
    host,
    isErrorEventData,
    isErrorHostData,
    isErrorUserData,
    isUserRCA,
    userData,
    userId,
  ]);

  const loginUser = useCallback(() => {
    dispatch(accountActions.setRedirectPath(pathname));
    navigate(NavigationId.SIGN_IN + "?subscriber", {
      state: { preLoginStage: LoginStage.PHONE },
    });
  }, [dispatch, pathname, navigate]);

  const roleTicketExists = useMemo(
    () => roleTicket && roleTicket.customTicketId === "",
    [roleTicket]
  );

  const viewQRCode = useCallback(() => {
    if (event && userData) {
      dispatch(DataLoaders.user(userData.uid));
      navigate(`/e/${event.id}/i/${userData.uid}/myTicket`, {
        state: { eventId: event.id, userId: userData.uid },
      });
    } else {
      alert(
        "You are not eligible to view QR code." +
          " This may be because you have not received a ticket yet."
      );
    }
  }, [dispatch, event, navigate, userData]);

  const confirmationSummaryTitle = useMemo(
    () =>
      roleTicketExists
        ? "Organizer Invite"
        : ticketReceipts.length > 0
          ? "Order Confirmation"
          : "Confirmation",
    [roleTicketExists, ticketReceipts.length]
  );

  const renderConfirmationSummary = useCallback(
    (event: Event, host: AccountData) => (
      <>
        <div className="AlignedRow" style={{ gap: 14 }}>
          <EventItemImage item={event} size={65} scaleImage />
          <div className="ColumnNormal" style={{ gap: 4 }}>
            <span className="bodyMedium TwoLineText" style={theme.PrimaryText}>
              {event.title}
            </span>
            <span className="bodySubtext">
              {isProductConfirmation
                ? host.fullName
                : GetDateWithTime(event.start, true)}
            </span>
          </div>
        </div>
        {numOfTickets > 0 &&
        (mappedTickets.size > 0 || ticketReceipts.length > 0) ? (
          <FullEventConfirmationReceipts
            ticketReceipts={ticketReceipts}
            mappedTickets={mappedTickets}
            customTickets={event.customTickets}
          />
        ) : numOfPendingTickets === 0 && !roleTicketExists ? (
          <span className="bodySubtext">
            No {isProductConfirmation ? "Orders" : "Tickets"} Found
          </span>
        ) : null}
      </>
    ),
    [
      isProductConfirmation,
      mappedTickets,
      numOfPendingTickets,
      numOfTickets,
      roleTicketExists,
      theme.PrimaryText,
      ticketReceipts,
    ]
  );

  const renderContactUs = useMemo(
    () => (
      <span
        style={{ ...theme.LinkColor, cursor: "pointer" }}
        onClick={() => window.open(MARKIT_CONTACT_US)}
      >
        Contact Us Page
      </span>
    ),
    [theme.LinkColor]
  );

  const renderConfirmationHeaderSection = useMemo(
    () =>
      userData ? (
        <div className="ColumnNormal" style={{ gap: 7 }}>
          {isTicketStatusPending ? (
            <div className="AlignedRow" style={{ gap: 10 }}>
              <span className="NameAndNumber" style={theme.PrimaryText}>
                Pending Approval
              </span>
              <Icon
                icon={"mdi:dots-circle"}
                height={20}
                style={theme.PrimaryText}
              />
            </div>
          ) : (
            <span className="NameAndNumber" style={theme.PrimaryText}>
              {numOfTickets > 0
                ? "Confirmation Details"
                : isProductConfirmation
                  ? "No Orders"
                  : "No Tickets"}
            </span>
          )}
          {numOfTickets > 0 && userData ? (
            <div className="ColumnNormal" style={{ gap: 14 }}>
              <div className="ColumnNormal" style={{ gap: 4 }}>
                {!isTicketStatusPending &&
                !isProductConfirmation &&
                numOfTickets > 0 ? (
                  <span className="bodySubtext">
                    View or share your tickets below.
                  </span>
                ) : null}
                {numOfPendingTickets > 0 ? (
                  <span className="bodySubtext">
                    We&apos;ll send you a text to{" "}
                    <span
                      style={{
                        ...theme.PrimaryText,
                        fontWeight: 500,
                      }}
                    >
                      {formatPhoneNumber(userData.phoneNumber)}
                    </span>{" "}
                    if you&apos;re approved.
                  </span>
                ) : null}
              </div>
              {showTwilioIssuesMessage ? (
                <div className="FinalErrorContainer" style={theme.DividerColor}>
                  <div
                    className="AlignedRow"
                    style={{ paddingInline: 14, gap: 7 }}
                  >
                    <Icon
                      icon="ion:alert-circle"
                      height={16}
                      style={{ color: Colors.RED3 }}
                    />
                    <span className="bodyMedium" style={{ color: Colors.RED3 }}>
                      Carrier Network Issue
                    </span>
                  </div>
                  <hr style={theme.DividerColor} />
                  <div className="bodySubtext" style={{ paddingInline: 14 }}>
                    Your carrier network is currently experiencing issues. If
                    you don&apos;t receive a text with your{" "}
                    {isProductConfirmation ? "order" : "ticket"}, please contact
                    support{" "}
                    <a
                      href={`sms:${MARKIT_HOTLINE_NUMBER}`}
                      style={theme.PrimaryText}
                    >
                      {"(" + formatPhoneNumber(MARKIT_HOTLINE_NUMBER) + ")"}
                    </a>{" "}
                    for assistance.
                  </div>
                </div>
              ) : optedOut || optedOutCreator ? (
                <OptedOutBanner
                  host={host}
                  optedOut={optedOut}
                  optedOutCreator={optedOutCreator}
                />
              ) : null}
            </div>
          ) : (
            <span className="bodySubtext">
              If you believe there was a mistake or error, visit our{" "}
              {renderContactUs} for assistance.
            </span>
          )}
        </div>
      ) : null,
    [
      userData,
      isTicketStatusPending,
      theme.PrimaryText,
      theme.DividerColor,
      numOfTickets,
      numOfPendingTickets,
      showTwilioIssuesMessage,
      isProductConfirmation,
      optedOut,
      optedOutCreator,
      host,
      renderContactUs,
    ]
  );

  const waitingForEventData =
    Boolean(eventId) && event == null && !isErrorEventData;
  const waitingForUserData =
    Boolean(userId) && userData == null && !isErrorUserData;
  const waitingForHostData = event != null && host == null && !isErrorHostData;

  const isLoadingPage = useMemo(
    () =>
      loading ||
      isLoadingEventData ||
      isLoadingUserData ||
      isLoadingHostData ||
      waitingForEventData ||
      waitingForUserData ||
      waitingForHostData,
    [
      loading,
      isLoadingEventData,
      isLoadingUserData,
      isLoadingHostData,
      waitingForEventData,
      waitingForUserData,
      waitingForHostData,
    ]
  );

  if (isLoadingPage) {
    return <LoadingScreen isLoadingPage={isLoadingPage} />;
  }
  if (
    isErrorEventData ||
    isErrorHostData ||
    isErrorUserData ||
    event == null ||
    host == null
  ) {
    return <LoadingScreen error />;
  }

  const renderCalendar = (
    <Calendar
      event={event}
      full={true}
      hasNoTickets={numOfTickets === 0}
      numOfTickets={numOfTickets}
      hideLocation={event.privateLocation && numOfApprovedTickets === 0}
      pending={isTicketStatusPending}
      loading={loading}
    />
  );

  return (
    <div>
      <div ref={refContainer} className="WebApp" style={theme.SubComponentBG}>
        <div className="WebContainer">
          <TopHeader eventTheme={event.theme} />
          <div
            className="WebColumnContainer"
            style={{ paddingInline: isDesktopSize ? 0 : 14 }}
          >
            <div className="RowNormal" style={{ gap: 60 }}>
              <div className="ColumnNormal" style={styles.leftContainer}>
                {!isDesktopSize && !roleTicketExists ? (
                  <>
                    <ExpandableBox>
                      <div className="ColumnNormal" style={{ gap: 14 }}>
                        <div
                          className="AlignedRowSelect"
                          style={{ gap: 4 }}
                          onClick={() =>
                            setShowConfirmationMobile(!showConfirmationMobile)
                          }
                        >
                          <span
                            className="bodyMedium"
                            style={theme.PrimaryText}
                          >
                            {showConfirmationMobile ? "Hide" : "Show"}{" "}
                            {confirmationSummaryTitle}
                          </span>
                          <DropdownArrowIcon
                            isOpen={showConfirmationMobile}
                            height={14}
                            color={theme.PrimaryText?.color}
                          />
                        </div>
                        {showConfirmationMobile
                          ? renderConfirmationSummary(event, host)
                          : null}
                      </div>
                    </ExpandableBox>
                    <HorizontalDivider altMargin={0} eventTheme={event.theme} />
                  </>
                ) : null}
                {userData &&
                numOfTickets > 0 &&
                !roleTicketExists &&
                !isTicketStatusPending ? (
                  <>
                    <div className="ColumnNormal" style={{ gap: 7 }}>
                      <div className="AlignedRow" style={{ gap: 10 }}>
                        <span
                          className="NameAndNumber"
                          style={theme.PrimaryText}
                        >
                          Thank You, {userData.fullName}
                        </span>
                        <Icon
                          icon="ion:checkmark-circle"
                          height={20}
                          style={{ color: Colors.GREEN2 }}
                        />
                      </div>
                      <div className="ColumnNormal" style={{ gap: 4 }}>
                        {ticketReceipts.length > 0 ? (
                          <span className="bodySubtext">
                            Your order was successfully placed!
                          </span>
                        ) : null}
                        {!showTwilioIssuesMessage &&
                        !optedOut &&
                        !optedOutCreator ? (
                          <span className="bodySubtext">
                            {userData.phoneNumber !== "" ? (
                              !isValidUSPhoneNumber(userData.phoneNumber) ? (
                                <>
                                  {isProductConfirmation
                                    ? "Save this link for easy access to your order."
                                    : "Save your tickets by adding to your calendar or saving this link."}
                                </>
                              ) : numOfPendingTickets > 0 &&
                                isTicketStatusPending ? (
                                <></>
                              ) : (
                                <>
                                  {ticketReceipts.length > 0
                                    ? "An order confirmation"
                                    : "A confirmation"}{" "}
                                  was texted to{" "}
                                  <span
                                    style={{
                                      ...theme.PrimaryText,
                                      fontWeight: "500",
                                    }}
                                  >
                                    {formatPhoneNumber(userData.phoneNumber)}
                                  </span>
                                  .
                                  <br />
                                </>
                              )
                            ) : null}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <HorizontalDivider altMargin={0} eventTheme={event.theme} />
                  </>
                ) : null}
                {!isProductConfirmation ? (
                  <>
                    {roleTicket && roleTicketExists ? (
                      <FullEventOrganizerConfirmation
                        roleTicket={roleTicket}
                        roleUserData={roleUserData}
                        alreadyHaveRole={alreadyHaveRole ?? false}
                        event={event}
                        numOfTickets={numOfTickets}
                        viewQRCode={viewQRCode}
                      />
                    ) : (
                      <div className="ColumnNormal" style={{ gap: 14 }}>
                        {renderConfirmationHeaderSection}
                        <div className="ColumnNormal" style={{ gap: 10 }}>
                          {!isTicketStatusPending ? (
                            <RectangleButton
                              buttonLabel={
                                numOfTickets > 0
                                  ? "Your Ticket" +
                                    (numOfTickets > 1 ? "s" : "")
                                  : "No Tickets"
                              }
                              onPress={viewQRCode}
                              altColor={theme.ButtonBG.backgroundColor}
                              altTextColor={theme.ButtonText.color}
                              altPaddingVert={14}
                              altBorderRadius={12}
                              disabled={
                                numOfTickets === 0 || isTicketStatusPending
                              }
                              containerStyles={{
                                border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
                              }}
                            />
                          ) : null}
                          <div className="AlignedRow" style={{ gap: 10 }}>
                            <FullEventManageTickets
                              event={event}
                              userId={userId}
                              mainUserTicket={userTicket}
                              numOfTickets={numOfTickets}
                              isTicketStatusPending={isTicketStatusPending}
                            />
                            {renderCalendar}
                          </div>
                        </div>
                      </div>
                    )}
                    <HorizontalDivider altMargin={0} eventTheme={event.theme} />
                  </>
                ) : null}
                {loggedIn !== LoginState.LOGGED_IN && !roleTicketExists ? (
                  <>
                    <div className="ColumnNormal" style={{ gap: 14 }}>
                      <div className="ColumnNormal" style={{ gap: 7 }}>
                        <span
                          className="NameAndNumber"
                          style={theme.PrimaryText}
                        >
                          Sign In
                        </span>
                        <div className="bodySubtext">
                          Sign in to see your{" "}
                          {isProductConfirmation
                            ? "orders"
                            : "tickets or requests"}{" "}
                          at any time.
                        </div>
                      </div>
                      <RectangleButton
                        buttonLabel="Sign In"
                        onPress={loginUser}
                        altColor={theme.NewPrimaryBG.backgroundColor}
                        altTextColor={theme.PrimaryText.color}
                        altPaddingVert={14}
                        altBorderRadius={12}
                        containerStyles={{
                          border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
                        }}
                      />
                    </div>
                    <HorizontalDivider altMargin={0} eventTheme={event.theme} />
                  </>
                ) : null}
                <div className="ColumnNormal">
                  <div
                    className="ColumnNormal"
                    style={{ gap: isMediaProduct ? 20 : 14 }}
                  >
                    {(isProductConfirmation
                      ? numOfApprovedTickets
                      : numOfTickets) > 0 || roleTicketExists ? (
                      <div className="ColumnNormal" style={{ gap: 7 }}>
                        <div
                          className="NameAndNumber"
                          style={theme.PrimaryText}
                        >
                          {isProductConfirmation ? "Order" : "Event"} Details
                        </div>
                        <span className="bodySubtext">
                          {isProductConfirmation ? (
                            numOfTickets > 0 ? (
                              "You can access the product below."
                            ) : (
                              <span>
                                No order found. If you believe there was a
                                mistake or error, visit our {renderContactUs}{" "}
                                for assistance.
                              </span>
                            )
                          ) : (
                            `View the event page for ${event.title} for more details.`
                          )}
                        </span>
                      </div>
                    ) : (
                      renderConfirmationHeaderSection
                    )}
                    {isProductConfirmation ? (
                      numOfApprovedTickets > 0 ? (
                        isMediaProduct ? (
                          <ConfirmationProductMedia
                            product={event}
                            creatorName={host.fullName}
                            locked={isMediaLocked}
                            lockedContent={
                              isUSAttendee ? (
                                <span
                                  role="button"
                                  className="bodySubtext"
                                  onClick={loginUser}
                                  style={{
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                  }}
                                >
                                  Sign in to access the product
                                </span>
                              ) : (
                                <UnlockProductByPhoneNumber
                                  productId={event.id}
                                  hardTheme={event.theme}
                                  onUnlocked={() => setUnlockedByPhone(true)}
                                  alternateSubtext="Enter your phone number to access the product"
                                />
                              )
                            }
                          />
                        ) : (
                          <div className="AlignedRow" style={{ gap: 14 }}>
                            <EventItemImage item={event} size={65} scaleImage />
                            <div
                              className="AlignedRowSpaced"
                              style={{ flex: 1 }}
                            >
                              <div className="ColumnNormal" style={{ gap: 4 }}>
                                <span
                                  className="bodyMedium TwoLineText"
                                  style={theme.PrimaryText}
                                >
                                  {event.title}
                                </span>
                                <span className="bodySubtext">
                                  {event.description || event.eventType}
                                </span>
                              </div>
                            </div>
                          </div>
                        )
                      ) : null
                    ) : (
                      <FullEventTimeLocation
                        event={event}
                        isRCABool={isRCABool}
                        numOfApprovedTickets={numOfApprovedTickets}
                      />
                    )}
                    <RectangleButton
                      buttonLabel={
                        showDownloadButton
                          ? isDownloading
                            ? "Downloading..."
                            : `Download ${event.eventType}`
                          : isProductConfirmation
                            ? "Product Page"
                            : "Event Page"
                      }
                      onPress={
                        showDownloadButton ? downloadContent : onViewEventClick
                      }
                      loading={showDownloadButton && isDownloading}
                      altColor={theme.NewPrimaryBG.backgroundColor}
                      altTextColor={theme.PrimaryText.color}
                      altPaddingVert={14}
                      altBorderRadius={12}
                      containerStyles={{
                        border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
                      }}
                      iconRight={
                        showDownloadButton ? (
                          <Icon
                            icon="ion:download"
                            height={16}
                            style={{ color: theme.PrimaryText.color }}
                          />
                        ) : (
                          <Icon
                            icon="ion:arrow-forward"
                            height={16}
                            style={{
                              color: theme.PrimaryText.color,
                              transform: "rotateY(0deg) rotate(-45deg)",
                            }}
                          />
                        )
                      }
                    />
                  </div>
                </div>
                <HorizontalDivider altMargin={0} eventTheme={event.theme} />
                <div className="ColumnNormal" style={{ gap: 14 }}>
                  <div
                    className="NameAndNumber"
                    style={{ ...theme.PrimaryText }}
                  >
                    More Options
                  </div>
                  <FullEventHosts
                    final
                    host={host}
                    hostAndCohosts={hostAndCohosts}
                    showUsername={isProductConfirmation}
                  />
                  {host &&
                  host.customer.phone !== "" &&
                  userPhoneVerificationStatus(host) ===
                    TwilioPhoneVerificationStatus.APPROVED ? (
                    <Banner
                      type={BannerType.TEXTHOST}
                      hostPhone={host.customer.phone}
                      bannerStyles={{
                        paddingBlock: 14,
                        paddingInline: 10,
                        borderRadius: 12,
                      }}
                    />
                  ) : (
                    <Banner
                      type={BannerType.TEXTHOST_FREE}
                      bannerStyles={{ paddingBlock: 14, borderRadius: 12 }}
                      eventType={event.eventType}
                    />
                  )}
                </div>
                {!event.hideResponses ? (
                  <>
                    <HorizontalDivider altMargin={0} eventTheme={event.theme} />
                    <GuestListSummary event={event} isConfirmation />
                  </>
                ) : null}
                {!roleTicketExists ? (
                  <>
                    <HorizontalDivider altMargin={0} eventTheme={event.theme} />
                    <CreatorTextManual host={host} />
                  </>
                ) : null}
              </div>
              {isDesktopSize ? (
                <ExpandableBox>
                  <StandardBorderedContainer
                    containerStyles={{
                      ...styles.rightContainer,
                      ...theme.NewPrimaryBG,
                    }}
                  >
                    <div className="ColumnNormal" style={{ gap: 14 }}>
                      <span className="sectionTitle" style={theme.PrimaryText}>
                        {confirmationSummaryTitle}
                      </span>
                      <HorizontalDivider
                        altMargin={0}
                        eventTheme={event.theme}
                      />
                      {renderConfirmationSummary(event, host)}
                    </div>
                  </StandardBorderedContainer>
                </ExpandableBox>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <Footer showRCA creatorData={host} />
    </div>
  );
});

export default FullEventConfirmation;
